import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { PropsWithChildren } from 'react'
import noop from 'lib/function/noop'

export interface CalendarRecommendationsView {
  checkIn?: string
  monthIndex?: string
}

interface CalendarRecommendationsViewContextValue {
  view: CalendarRecommendationsView | undefined
  hasRenderableRecommendations: boolean
  open: (view: CalendarRecommendationsView) => void
  close: () => void
  setHasRenderableRecommendations: (hasRenderableRecommendations: boolean) => void
}

export const CalendarRecommendationsViewContext = createContext<CalendarRecommendationsViewContextValue>({
  view: undefined,
  hasRenderableRecommendations: false,
  open: noop,
  close: noop,
  setHasRenderableRecommendations: noop,
})

export function CalendarRecommendationsViewProvider({ children }: PropsWithChildren) {
  const [view, setView] = useState<CalendarRecommendationsView | undefined>()
  const [hasRenderableRecommendations, setHasRenderableRecommendations] = useState(false)

  const open = useCallback((next: CalendarRecommendationsView) => {
    setHasRenderableRecommendations(false)
    setView({
      checkIn: next.checkIn,
      monthIndex: next.monthIndex,
    })
  }, [])

  const close = useCallback(() => {
    setHasRenderableRecommendations(false)
    setView(undefined)
  }, [])

  const value = useMemo(() => ({
    view,
    hasRenderableRecommendations,
    open,
    close,
    setHasRenderableRecommendations,
  }), [close, hasRenderableRecommendations, open, view])

  return (
    <CalendarRecommendationsViewContext.Provider value={value}>
      {children}
    </CalendarRecommendationsViewContext.Provider>
  )
}

export function useCalendarRecommendationsView() {
  return useContext(CalendarRecommendationsViewContext)
}
